
import Vue from 'vue';
import { mapGetters, mapState, mapActions } from 'vuex';

import { formatNumber } from '@/i18n';

import FormInput from '@/components/controls/form-input.vue';
import SelectInput from '@/components/controls/select-input.vue';
import SubmitButton from '@/components/controls/submit-button.vue';
import NotificationBanner from '@/components/blocks/notification-banner.vue';
import Publish from '@/components/forms/publish.vue';
import ImageUploadControl from '@/components/controls/image-upload.vue';
import PracticeSelectionControl from '@/components/controls/practice-selection.vue';
import Autocomplete, { AutocompleteOption } from '@/components/controls/autocomplete.vue';
import LanguagePicker from '@/components/controls/language-picker.vue';
import SendWelcomeEmailModal from '@/components/forms/send-welcome-email-modal.vue';
import SendNewDeviceEmailModal from '@/components/forms/send-new-device-email-modal.vue';
import Modal from '@/components/layout/modal.vue';

import { UserStatus, UserStatuses } from '@/store/models/user';
import Distributor from '@/store/modules/distributors/distributor';
import { UsersActions } from '@/store/modules/users/types';
import { ListStatus, RootActions } from '@/store/types';
import { UserRole } from '@/store/modules/user/profile/types';
import { PracticeStatus } from '@/store/modules/practices/types';
import Country from '@/store/models/country';
import { UserActions } from '@/store/modules/user/types';
import { SendWelcomeEmailParams } from '@/api/endpoints/users';
import Email from '@/store/modules/settings/email';

export default Vue.extend({
  name: 'EditUserView',
  inject: {
    $validator: '$validator',
  },
  props: {
    add: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    FormInput,
    SelectInput,
    SubmitButton,
    NotificationBanner,
    Publish,
    ImageUploadControl,
    PracticeSelectionControl,
    Autocomplete,
    LanguagePicker,
    SendWelcomeEmailModal,
    SendNewDeviceEmailModal,
    Modal,
  },
  data() {
    return {
      repeatedPassword: '',
      countryInput: '',
      regionInput: '',
      isSendWelcomeMailModalOpen: false,
      welcomeModalTemplateKey: 'welcome' as 'welcome' | 'welcome-simple',
      isWelcomeMailPromptOpen: false,
      distributorRegionsFieldError: false,
      distributorInput: '',
      distributorOrgFieldError: false,
      isSendWelcomeSimpleMailModalOpen: false,
      isSendNewDeviceEmailModalOpen: false,
    };
  },
  computed: {
    UserRole() {
      return UserRole;
    },
    UserStatus() {
      return UserStatus;
    },
    displayRole() {
      // Use actualRole if available, otherwise fall back to role
      return this.user?.actualRole || this.user?.role || UserRole.User;
    },
    isLoggedInDistributor(): boolean {
      return this.isDistributor;
    },
    showDistributorRegionsEditor(): boolean {
      return this.isSuperAdmin && this.displayRole === UserRole.Distributor;
    },
    /** Admins assign a distributor org when the user has the Distributor role. */
    showDistributorOrganizationPicker(): boolean {
      return (this.isSuperAdmin || this.isAdmin) && this.displayRole === UserRole.Distributor;
    },
    ...mapState('distributors', {
      distributorsList: 'list',
    }),
    ...mapGetters('distributors', {
      distributorsListLoading: 'listIsLoading',
    }),
    /** Distributors are scoped by regions, not the explicit country list. */
    showCountriesSection(): boolean {
      const r = this.displayRole;
      return r === UserRole.Admin || r === UserRole.SuperAdmin;
    },
    areRegionsLoaded(): boolean {
      return this.regionsStatus === ListStatus.Loaded;
    },
    ...mapState('user/profile', {
      currentUser: 'currentProfile',
    }),
    ...mapGetters('user/profile', ['isSuperAdmin', 'isAdmin', 'isDistributor']),
    ...mapState(['countries', 'countriesStatus', 'regions', 'regionsStatus']),
    ...mapState('settings', ['emails']),
    ...mapState('users', {
      user: 'editable',
      editableStatus: 'editableStatus',
    }),
    ...mapGetters('users', {
      isLoading: 'editableIsLoading',
      isSaving: 'editableIsSaving',
      isSaved: 'editableIsSaved',
      isFailed: 'editableIsFailed',
      isFetching: 'editableIsFetching',
      hasUnsavedChanges: 'editableHasUnsavedChanges',
    }),
    ...mapState('users', {
      isSendingWelcome: 'sendingWelcome',
    }),
    isInactive() {
      return this.user.status === UserStatus.Inactive;
    },
    isActive() {
      return this.user.status === UserStatus.Active;
    },
    isWaitingActivation() {
      return this.user.status === UserStatus.WaitingActivation;
    },
    hasPracticeDeleted() {
      return (
        this.user.status === UserStatus.Inactive &&
        this.user.practice &&
        this.user.practice.status === PracticeStatus.Inactive
      );
    },
    userStatus() {
      if (this.hasPracticeDeleted) {
        return this.$t('Practice Deleted');
      }

      return UserStatuses[this.user.status];
    },
    countriesOptions(): AutocompleteOption[] {
      const countriesIds = this.user.countries.map(({ id }) => id);

      return this.countries
        .filter((country) => {
          return countriesIds.indexOf(country.id) === -1;
        })
        .map((country) => ({
          label: country.name,
          value: country.id,
        }))
        .filter((option) => option.label.toLowerCase().indexOf(this.countryInput.toLowerCase()) > -1);
    },
    /** Root `regions` is always an array after LOAD_REGIONS; guard for bad/partial state. */
    regionsCatalog(): { id: number | string; name: string }[] {
      const r = this.regions as unknown;
      return Array.isArray(r) ? r : [];
    },
    regionsOptions(): AutocompleteOption[] {
      const chosen = new Set(this.user.regions.map((r) => Number(r.id)));
      return this.regionsCatalog
        .filter((reg) => !chosen.has(Number(reg.id)))
        .map((reg) => ({ label: reg.name, value: Number(reg.id) }))
        .filter((option) => option.label.toLowerCase().indexOf(this.regionInput.toLowerCase()) > -1);
    },
    selectedDistributorName(): string {
      const id = this.user.distributorId;
      if (!id) {
        return '';
      }
      const list = this.distributorsList as Distributor[];
      const d = list.find((x) => x.id === id);
      return d ? d.name : (this.$t('Distributor #%(id)s', { id: String(id) }) as string);
    },
    distributorOptions(): AutocompleteOption[] {
      const q = this.distributorInput.toLowerCase();
      const chosenId = this.user.distributorId;
      return (this.distributorsList as Distributor[])
        .filter((d) => d.id !== chosenId)
        .filter((d) => d.name.toLowerCase().indexOf(q) !== -1)
        .map((d) => ({ label: d.name, value: d.id }));
    },
    areCountriesLoaded(): boolean {
      return this.countriesStatus === ListStatus.Loaded;
    },
    isCurrentUser(): boolean {
      return this.currentUser.id === this.user.id;
    },
    welcomeEmail(): Email | undefined {
      return (this.emails as Email[]).find((email) => email.key === 'welcome');
    },
    /** Template used for "Approve User" (welcome-simple); Resend modal still uses welcome */
    approveWelcomeEmail(): Email | undefined {
      return (this.emails as Email[]).find((email) => email.key === 'welcome-simple');
    },
    newDeviceEmail(): Email | undefined {
      return (this.emails as Email[]).find((email) => email.key === 'new-device-added');
    },
  },
  methods: {
    formatNumber,
    ...mapActions('users', {
      updateUser: UsersActions.UPDATE_EDITABLE,
      save: UsersActions.SAVE_EDITABLE,
      removeUser: UsersActions.DELETE,
      restoreUser: UsersActions.RESTORE,
      sendWelcomeMail: UsersActions.SEND_WELCOME_MAIL,
    }),
    ...mapActions('user', {
      impersonateAction: UserActions.IMPERSONATE,
    }),
    updateUserRole(role: UserRole) {
      const prev = this.displayRole;
      this.updateUser({
        actualRole: role,
        role: role,
        ...(role !== UserRole.Distributor ? { regions: [], distributorId: null } : {}),
        ...(role === UserRole.Distributor ? { countries: [] } : {}),
        ...(role === UserRole.Distributor && prev !== UserRole.Distributor ? { distributorId: null } : {}),
      });
      if (role !== UserRole.Distributor) {
        this.distributorRegionsFieldError = false;
        this.distributorOrgFieldError = false;
        this.distributorInput = '';
      }
    },
    regionName(regionId: number): string {
      const r = this.regionsCatalog.find((item) => Number(item.id) === Number(regionId));
      return r ? r.name : String(regionId);
    },
    addRegion(option: AutocompleteOption) {
      const id = Number(option.value);
      if (!Number.isFinite(id) || this.user.regions.some((r) => Number(r.id) === id)) {
        return;
      }
      const catalog = this.regionsCatalog.find((reg) => Number(reg.id) === id);
      this.updateUser({
        regions: [...this.user.regions, { id, name: catalog ? String(catalog.name) : '' }],
      });
      this.clearDistributorRegionsFieldError();
    },
    removeRegion(regionId: number) {
      this.updateUser({
        regions: this.user.regions.filter((r) => Number(r.id) !== Number(regionId)),
      });
    },
    clearDistributorRegionsFieldError() {
      if (this.user.regions.length >= 1) {
        this.distributorRegionsFieldError = false;
      }
    },
    saveUser() {
      const publish = this.$refs.userPublish as { errorMessage?: (msg: { title: string; body: string }) => void };
      if (this.showDistributorRegionsEditor && this.user.regions.length < 1) {
        this.distributorRegionsFieldError = true;
        publish?.errorMessage?.({
          title: this.$t('Error') as string,
          body: this.$t('Distributors must be assigned at least one region.') as string,
        });
        return;
      }
      this.distributorRegionsFieldError = false;
      /** Any user with the Distributor role must have a distributor organization (not only when admins see the picker). */
      if (this.displayRole === UserRole.Distributor && (!this.user.distributorId || this.user.distributorId < 1)) {
        this.distributorOrgFieldError = true;
        publish?.errorMessage?.({
          title: this.$t('Error') as string,
          body: this.$t('Please select a distributor organization.') as string,
        });
        return;
      }
      this.distributorOrgFieldError = false;
      this.save();
    },
    selectDistributorOrganization(option: AutocompleteOption) {
      const id = Number(option.value);
      if (!Number.isFinite(id)) {
        return;
      }
      this.updateUser({ distributorId: id });
      this.distributorInput = '';
      this.distributorOrgFieldError = false;
    },
    clearDistributorOrganization() {
      this.updateUser({ distributorId: null });
    },
    addCountry(option: AutocompleteOption) {
      const country = this.countries.find(({ id }) => id === option.value);

      if (country) {
        this.updateUser({
          countries: [
            ...this.user.countries,
            Country.fromJSON({
              id: country.id,
              code: country.code,
              name: country.name,
            }),
          ],
        });
      }
    },
    removeCountry(countryId: number) {
      this.updateUser({
        countries: [...this.user.countries].filter(({ id }) => id !== countryId),
      });
    },
    remove() {
      this.removeUser({ id: this.user.id });
    },
    restore() {
      this.restoreUser({ id: this.user.id });
    },
    impersonate() {
      this.impersonateAction({ id: this.user.id });
    },
    closeWelcomeMailPrompt() {
      const { open_welcome_email_modal, ...query } = this.$route.query;

      this.isWelcomeMailPromptOpen = false;
      this.$router.replace({ query });
    },
    openWelcomeMailModal() {
      this.welcomeModalTemplateKey = 'welcome';
      this.isSendWelcomeMailModalOpen = true;
      this.closeWelcomeMailPrompt();
    },
    openResendWelcomeModal() {
      this.welcomeModalTemplateKey = 'welcome-simple';
      this.isSendWelcomeSimpleMailModalOpen = true;
    },
    openNewDeviceEmailModal() {
      this.isSendNewDeviceEmailModalOpen = true;
    },
    async approveUser() {
      if (!this.approveWelcomeEmail) {
        return;
      }

      const email = this.approveWelcomeEmail;
      const languageCode = this.user.languageCode;

      // Get the translated content or fall back to default
      const subject =
        typeof email.translations.subject[languageCode] !== 'undefined'
          ? email.translations.subject[languageCode]
          : email.subject;
      const body =
        typeof email.translations.body[languageCode] !== 'undefined'
          ? email.translations.body[languageCode]
          : email.body;
      const button =
        typeof email.translations.button[languageCode] !== 'undefined'
          ? email.translations.button[languageCode]
          : email.button;
      const help =
        typeof email.translations.help[languageCode] !== 'undefined'
          ? email.translations.help[languageCode]
          : email.help;

      const welcomeEmailParams: SendWelcomeEmailParams = {
        id: this.user.id,
        send_to_admin: false,
        subject,
        body,
        button,
        help,
        reply_to: undefined,
      };

      await this.sendWelcomeMail(welcomeEmailParams);
    },
  },
  created() {
    this.$store.dispatch(RootActions.LOAD_REGIONS, { force: false });
  },
  watch: {
    '$route.query.open_welcome_email_modal': {
      handler(value, prevValue) {
        if (value !== prevValue) {
          if (value === '1') {
            this.isWelcomeMailPromptOpen = true;
          } else {
            this.isWelcomeMailPromptOpen = false;
          }
        }
      },
      immediate: true,
    },
  },
  beforeRouteLeave(to, from, next) {
    if (this.hasUnsavedChanges) {
      const answer = window.confirm(this.$t('Do you really want to leave? You have unsaved changes!'));

      if (!answer) {
        next(false);
        return;
      }
    }

    next();
  },
});
